// Client-side mirror of internal/personid/personid.go. Same algorithms; we
// need a TS port so the AddMemberModal can give immediate red/green
// feedback as the user types instead of waiting for a server round-trip on
// every keystroke. The server stays authoritative — frontend validation is
// for UX only and the SPA still sends the raw value across the wire.
//
// Year expansion (the user-asked feature): input "800101-1234" comes back
// as canonical "198001011234" so it's clear which century we resolved to.
// '+' separator (Sweden) means the holder turned 100 (so 24+ → 1924);
// otherwise pick 19XX/20XX based on the cutoff year.

export type PersonIdCountry = 'SE' | 'NO' | 'DK' | 'FI';

export type PersonIdReason =
  | ''
  | 'invalid_format'
  | 'invalid_date'
  | 'invalid_checksum'
  | 'unsupported_country';

export interface PersonIdResult {
  valid: boolean;
  canonical: string;
  reason: PersonIdReason;
}

const SUPPORTED_COUNTRIES: ReadonlySet<PersonIdCountry> = new Set([
  'SE',
  'NO',
  'DK',
  'FI',
]);

export function validatePersonId(country: string, raw: string): PersonIdResult {
  const c = country.trim().toUpperCase() as PersonIdCountry;
  if (!SUPPORTED_COUNTRIES.has(c)) {
    return { valid: false, canonical: '', reason: 'unsupported_country' };
  }
  switch (c) {
    case 'SE': return validateSE(raw);
    case 'NO': return validateNO(raw);
    case 'DK': return validateDK(raw);
    case 'FI': return validateFI(raw);
  }
}

// pickCenturyByCutoff expands a 2-digit year to 4 by assuming "person is
// alive". Mirrors personid.go's helper. now defaults to today; injectable
// for unit tests.
export function pickCenturyByCutoff(yy: number, now: Date = new Date()): number {
  if (yy < 0 || yy > 99) return -1;
  const currentTwo = now.getFullYear() % 100;
  return yy <= currentTwo ? 2000 + yy : 1900 + yy;
}

// --- Sweden ----------------------------------------------------------------

function validateSE(raw: string): PersonIdResult {
  raw = raw.trim();
  if (!raw) return invalid('invalid_format');

  let plusSep = false;
  let digits = '';
  for (const ch of raw) {
    if (ch === '-' || ch === ' ') continue;
    if (ch === '+') {
      plusSep = true;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      digits += ch;
      continue;
    }
    return invalid('invalid_format');
  }

  let year: number;
  let mmddIndivCheck: string;
  if (digits.length === 12) {
    year = parseInt(digits.slice(0, 4), 10);
    mmddIndivCheck = digits.slice(4);
  } else if (digits.length === 10) {
    const yy = parseInt(digits.slice(0, 2), 10);
    year = pickCenturyByCutoff(yy);
    if (plusSep) year -= 100;
    mmddIndivCheck = digits.slice(2);
  } else {
    return invalid('invalid_format');
  }

  if (mmddIndivCheck.length !== 8) return invalid('invalid_format');
  const month = parseInt(mmddIndivCheck.slice(0, 2), 10);
  const day = parseInt(mmddIndivCheck.slice(2, 4), 10);

  // Coordination numbers (samordningsnummer) shift day by +60.
  let calDay = day;
  if (day >= 61 && day <= 91) calDay = day - 60;
  if (!isValidDate(year, month, calDay)) return invalid('invalid_date');

  // Luhn over YYMMDDNNN (9 digits before the check digit). Note the 2-digit
  // year on the Luhn input even when the user supplied a 12-digit form —
  // Swedish algorithm doesn't include the century in the checksum.
  const yy2 = year % 100;
  const luhnInput =
    yy2.toString().padStart(2, '0') +
    month.toString().padStart(2, '0') +
    day.toString().padStart(2, '0') +
    mmddIndivCheck.slice(4, 7);
  const checkDigit = parseInt(mmddIndivCheck[7]!, 10);
  if (luhnCheck(luhnInput) !== checkDigit) return invalid('invalid_checksum');

  const canonical =
    year.toString().padStart(4, '0') +
    month.toString().padStart(2, '0') +
    day.toString().padStart(2, '0') +
    mmddIndivCheck.slice(4);
  return { valid: true, canonical, reason: '' };
}

function luhnCheck(digits: string): number {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = parseInt(digits[i]!, 10);
    if (i % 2 === 0) {
      d *= 2;
      if (d >= 10) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10;
}

// --- Norway ----------------------------------------------------------------

const NO_WEIGHTS_1 = [3, 7, 6, 1, 8, 9, 4, 5, 2];
const NO_WEIGHTS_2 = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

function validateNO(raw: string): PersonIdResult {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return invalid('invalid_format');

  const day = parseInt(digits.slice(0, 2), 10);
  const month = parseInt(digits.slice(2, 4), 10);
  const yy = parseInt(digits.slice(4, 6), 10);
  const individual = parseInt(digits.slice(6, 9), 10);
  if ([day, month, yy, individual].some(Number.isNaN)) return invalid('invalid_format');

  const year = norwegianCentury(yy, individual);
  if (year < 0) return invalid('invalid_date');

  // D-numbers (day += 40, foreign residents) and H-numbers (month += 40).
  let calDay = day;
  if (calDay >= 41 && calDay <= 71) calDay -= 40;
  let calMonth = month;
  if (calMonth >= 41 && calMonth <= 52) calMonth -= 40;
  if (!isValidDate(year, calMonth, calDay)) return invalid('invalid_date');

  if (!checkMod11(digits.slice(0, 9), digits[9]!, NO_WEIGHTS_1)) {
    return invalid('invalid_checksum');
  }
  if (!checkMod11(digits.slice(0, 10), digits[10]!, NO_WEIGHTS_2)) {
    return invalid('invalid_checksum');
  }
  return { valid: true, canonical: digits, reason: '' };
}

function norwegianCentury(yy: number, individual: number): number {
  if (individual >= 0 && individual <= 499) return 1900 + yy;
  if (individual >= 500 && individual <= 749) {
    if (yy >= 54 && yy <= 99) return 1800 + yy;
    if (yy >= 0 && yy <= 39) return 2000 + yy;
    return -1;
  }
  if (individual >= 500 && individual <= 999) {
    if (yy >= 0 && yy <= 39) return 2000 + yy;
    if (yy >= 40 && yy <= 99) return 1900 + yy;
    return -1;
  }
  return -1;
}

function checkMod11(digits: string, want: string, weights: number[]): boolean {
  if (digits.length !== weights.length) return false;
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += parseInt(digits[i]!, 10) * weights[i]!;
  }
  let check = 11 - (sum % 11);
  if (check === 11) check = 0;
  if (check === 10) return false;
  return check === parseInt(want, 10);
}

// --- Denmark ---------------------------------------------------------------

function validateDK(raw: string): PersonIdResult {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return invalid('invalid_format');

  const day = parseInt(digits.slice(0, 2), 10);
  const month = parseInt(digits.slice(2, 4), 10);
  const yy = parseInt(digits.slice(4, 6), 10);
  const seventh = parseInt(digits[6]!, 10);
  if ([day, month, yy, seventh].some(Number.isNaN)) return invalid('invalid_format');

  const year = danishCentury(yy, seventh);
  if (year < 0) return invalid('invalid_date');
  if (!isValidDate(year, month, day)) return invalid('invalid_date');
  return { valid: true, canonical: digits, reason: '' };
}

function danishCentury(yy: number, seventh: number): number {
  if (seventh <= 3) return 1900 + yy;
  if (seventh === 4 || seventh === 9) return yy <= 36 ? 2000 + yy : 1900 + yy;
  if (seventh >= 5 && seventh <= 8) return yy <= 57 ? 2000 + yy : 1800 + yy;
  return -1;
}

// --- Finland (HETU) --------------------------------------------------------

const FI_CHECK_TABLE = '0123456789ABCDEFHJKLMNPRSTUVWXY';

function validateFI(raw: string): PersonIdResult {
  raw = raw.trim().toUpperCase();
  if (raw.length !== 11) return invalid('invalid_format');
  for (let i = 0; i < 6; i++) {
    if (!isDigit(raw[i]!)) return invalid('invalid_format');
  }
  for (let i = 7; i < 10; i++) {
    if (!isDigit(raw[i]!)) return invalid('invalid_format');
  }
  const day = parseInt(raw.slice(0, 2), 10);
  const month = parseInt(raw.slice(2, 4), 10);
  const yy = parseInt(raw.slice(4, 6), 10);
  const sep = raw[6]!;
  const year = finnishCentury(yy, sep);
  if (year < 0) return invalid('invalid_format');
  if (!isValidDate(year, month, day)) return invalid('invalid_date');

  const core = parseInt(raw.slice(0, 6) + raw.slice(7, 10), 10);
  if (Number.isNaN(core)) return invalid('invalid_format');
  const expected = FI_CHECK_TABLE[core % 31];
  if (raw[10] !== expected) return invalid('invalid_checksum');
  return { valid: true, canonical: raw, reason: '' };
}

function finnishCentury(yy: number, sep: string): number {
  switch (sep) {
    case '+': return 1800 + yy;
    case '-': case 'Y': case 'X': case 'W': case 'V': case 'U': return 1900 + yy;
    case 'A': case 'B': case 'C': case 'D': case 'E': case 'F': return 2000 + yy;
    default: return -1;
  }
}

// --- shared helpers --------------------------------------------------------

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function isValidDate(year: number, month: number, day: number): boolean {
  if (year < 1800 || month < 1 || month > 12 || day < 1 || day > 31) return false;
  // Use UTC to avoid the local-timezone DST trap (a date built in local time
  // with a non-existent hour during DST transitions can wrap silently).
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  );
}

function invalid(reason: PersonIdReason): PersonIdResult {
  return { valid: false, canonical: '', reason };
}
